// Como os canais de venda se agrupam no resumo de Pedidos.
//
// A quebra vem do backend por (modulo, tipo_entrega) e é montada aqui. O que
// separa "entrega que o atendente digitou" de "entrega que o cliente fechou
// sozinho no cardápio" é justamente o tipo de entrega dentro do mesmo canal.

export const MODULOS_IMPORTACAO = ["Grandchef", "AnotaAi"];

// Canais em que o pedido chega pronto, sem ninguém do salão digitar. É o que o
// card de automação mede.
export const MODULOS_AUTOMATICOS = ["MesaQR", "Totem", "Cardapio", "CardapioPublico"];

const ROTULO_ENTREGA = {
  delivery: "Entregas",
  pickup: "Retiradas",
  mesa: "Mesas",
  balcao: "Balcão",
  local: "Balcão",
  viagem: "Viagem",
};

export const GRUPOS = [
  {
    key: "mesas",
    label: "Mesas",
    cor: "bg-blue-500",
    variacoes: [
      { key: "mesas", label: "Mesas", modulo: ["Mesas"] },
      { key: "mesaqr", label: "Mesa QR Code", modulo: ["MesaQR"], automatico: true },
    ],
  },
  {
    key: "comandas",
    label: "Comandas",
    cor: "bg-amber-500",
    variacoes: [
      { key: "comandas", label: "Comandas", modulo: ["Comandas"] },
    ],
  },
  {
    key: "delivery",
    label: "Delivery",
    cor: "bg-orange-500",
    variacoes: [
      { key: "entrega-atendente", label: "Entregas (atendente)", modulo: ["Delivery"], tipo: ["delivery"] },
      { key: "retirada-atendente", label: "Retiradas (atendente)", modulo: ["Delivery"], tipo: ["pickup"] },
      { key: "entrega-cardapio", label: "Entregas Cardápio Digital", modulo: ["Cardapio"], tipo: ["delivery"], automatico: true },
      { key: "retirada-cardapio", label: "Retiradas Cardápio Digital", modulo: ["Cardapio"], tipo: ["pickup"], automatico: true },
    ],
  },
  {
    key: "balcao",
    label: "Balcão",
    cor: "bg-purple-500",
    variacoes: [
      { key: "balcao", label: "Balcão", modulo: ["Balcao"] },
      { key: "totem", label: "Totem", modulo: ["Totem"], automatico: true },
    ],
  },
  {
    key: "marketplace",
    label: "Marketplace",
    cor: "bg-red-500",
    variacoes: [
      { key: "ifood", label: "iFood", modulo: ["iFood"], logo: "/logo-ifood.png" },
      { key: "keeta", label: "Keeta", modulo: ["Keeta"], logo: "/logo-keeta.png" },
      { key: "99food", label: "99Food", modulo: ["99Food"], logo: "/logo-99food.png" },
      { key: "deliverymuch", label: "Delivery Much", modulo: ["DeliveryMuch"], logo: "/logo-deliverymuch.png" },
      { key: "aiqfome", label: "aiqfome", modulo: ["Aiqfome"], logo: "/logo-aiqfome.png" },
    ],
  },
  {
    key: "importados",
    label: "Importados",
    cor: "bg-amber-500",
    importado: true,
    // As variações nascem do que o arquivo importado informou: com categoria,
    // vira a linha do módulo correspondente; sem categoria, fica "Importado".
    variacoes: [],
  },
];

const vazio = () => ({ count: 0, revenue: 0, concluidos: 0 });

function somar(alvo, linha) {
  alvo.count += linha.count || 0;
  alvo.revenue += linha.revenue || 0;
  alvo.concluidos += linha.concluidos || 0;
}

const ticketDe = (t) => (t.concluidos > 0 ? t.revenue / t.concluidos : 0);

function casa(variacao, linha) {
  if (variacao.modulo && !variacao.modulo.includes(linha.modulo)) return false;
  if (variacao.tipo && !variacao.tipo.includes(linha.tipo_entrega)) return false;
  return true;
}

/** Monta os grupos (com variações, ticket e participação) a partir do breakdown. */
export function montarGrupos(breakdown) {
  const linhas = (breakdown || []).map((l) => ({
    ...l,
    modulo: l.modulo === "CardapioPublico" ? "Cardapio" : l.modulo,
  }));
  const usadas = new Set();

  const grupos = GRUPOS.map((grupo) => {
    const totalGrupo = vazio();
    let variacoes = [];

    if (grupo.importado) {
      // Uma linha por categoria informada na importação.
      const porCategoria = new Map();
      linhas.forEach((linha, i) => {
        if (!MODULOS_IMPORTACAO.includes(linha.modulo)) return;
        usadas.add(i);
        const rotulo = ROTULO_ENTREGA[linha.tipo_entrega] || "Importado";
        if (!porCategoria.has(rotulo)) porCategoria.set(rotulo, { ...vazio(), tipos: new Set() });
        const alvo = porCategoria.get(rotulo);
        somar(alvo, linha);
        if (linha.tipo_entrega) alvo.tipos.add(linha.tipo_entrega);
        somar(totalGrupo, linha);
      });
      variacoes = [...porCategoria.entries()].map(([rotulo, dados]) => ({
        key: `importado-${rotulo.toLowerCase()}`,
        label: rotulo,
        automatico: false,
        count: dados.count,
        revenue: dados.revenue,
        ticket: ticketDe(dados),
        filtro: { modulo: MODULOS_IMPORTACAO, tipo_entrega: [...dados.tipos] },
      }));
    } else {
      variacoes = grupo.variacoes.map((variacao) => {
        const total = vazio();
        linhas.forEach((linha, i) => {
          if (MODULOS_IMPORTACAO.includes(linha.modulo)) return;
          if (!casa(variacao, linha)) return;
          usadas.add(i);
          somar(total, linha);
          somar(totalGrupo, linha);
        });
        return {
          ...variacao,
          count: total.count,
          revenue: total.revenue,
          ticket: ticketDe(total),
          filtro: { modulo: variacao.modulo, tipo_entrega: variacao.tipo || [] },
        };
      });
    }

    return {
      ...grupo,
      count: totalGrupo.count,
      revenue: totalGrupo.revenue,
      ticket: ticketDe(totalGrupo),
      variacoes: variacoes
        .map((v) => ({ ...v, pctDoGrupo: totalGrupo.revenue > 0 ? (v.revenue / totalGrupo.revenue) * 100 : 0 }))
        .sort((a, b) => b.count - a.count),
    };
  });

  // Nada pode sumir do resumo só porque não coube num grupo conhecido.
  const sobras = linhas.filter((_, i) => !usadas.has(i)).filter((l) => l.count > 0);
  if (sobras.length > 0) {
    const total = vazio();
    sobras.forEach((l) => somar(total, l));
    grupos.push({
      key: "outros",
      label: "Outros canais",
      cor: "bg-zinc-500",
      count: total.count,
      revenue: total.revenue,
      ticket: ticketDe(total),
      variacoes: sobras.map((l) => ({
        key: `outro-${l.modulo}-${l.tipo_entrega}`,
        label: l.modulo,
        count: l.count,
        revenue: l.revenue,
        ticket: ticketDe(l),
        pctDoGrupo: total.revenue > 0 ? (l.revenue / total.revenue) * 100 : 0,
        filtro: { modulo: [l.modulo], tipo_entrega: l.tipo_entrega ? [l.tipo_entrega] : [] },
      })),
    });
  }

  const totalGeral = grupos.reduce((s, g) => s + g.revenue, 0);
  return grupos.map((g) => ({
    ...g,
    pctDoTotal: totalGeral > 0 ? (g.revenue / totalGeral) * 100 : 0,
  }));
}

/** Quanto das vendas entrou sozinho, sem atendente digitando.
 *
 *  Pedido importado de outro sistema fica FORA da conta (decisão do dono):
 *  ele nasceu em outro PDV, então não é venda que o Núcleo poderia ter
 *  automatizado — mantê-lo no total afundaria o percentual sem significar nada.
 */
export function calcularAutomacao(breakdown) {
  const auto = vazio();
  const base = vazio();
  (breakdown || []).forEach((linha) => {
    if (MODULOS_IMPORTACAO.includes(linha.modulo)) return;
    somar(base, linha);
    if (MODULOS_AUTOMATICOS.includes(linha.modulo)) somar(auto, linha);
  });
  return {
    pedidos: auto.count,
    receita: auto.revenue,
    basePedidos: base.count,
    baseReceita: base.revenue,
    pctPedidos: base.count > 0 ? (auto.count / base.count) * 100 : 0,
    pctReceita: base.revenue > 0 ? (auto.revenue / base.revenue) * 100 : 0,
  };
}
